// M004-F0636 §4 (P0, E8 — WebUI #109 sublane (b), WEB-ENV-002) — the upload
// zone for the Envelope drawer: the whole zone is a drop target, the header
// button opens the file picker (multiple, image/jpeg,image/png,image/webp,application/pdf),
// and every file becomes its own independent, idempotent Spec v1.2.0 envelope
// upload via createUploadQueue (envelopeUploadModel.js): per-row progress
// (queued → uploading → stored / failed), the batch summary "N of M stored",
// a Retry that replays the SAME receipt_id, and the step-up contract — a
// STEP_UP_REQUIRED answer opens the StepUpDialog ONCE, the selected file set
// survives the challenge, and the queue resumes where it left off without a
// re-pick. A closed envelope shows the zone disabled with the closed notice;
// a deleted one disables upload entirely. Refusals render the server's
// verbatim message.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import StepUpDialog from '../auth/StepUpDialog';
import { captureBadge, CLOSED_UPLOAD_NOTICE } from './envelopeModel';
import { createUploadQueue, duplicatePlace, ENVELOPE_UPLOAD_ACCEPT } from './envelopeUploadModel';
import './EnvelopeUploadZone.css';

const DELETED_NOTICE = 'Uploading is disabled — this envelope is deleted.';

const stateLabel = (item) => {
  switch (item.state) {
    case 'queued': return 'Waiting…';
    case 'uploading': return `Uploading… ${Math.round((item.progress || 0) * 100)}%`;
    case 'stored': return 'Stored';
    case 'failed': return item.error;
    case 'duplicate': return item.error;
    case 'step-up': return 'Waiting for verification — Retry to continue';
    case 'invalid': return item.error;
    default: return item.state;
  }
};

// M004-F0744: `disabledNotice` switches the zone off for a reason the workspace knows (Ledger down, locked, still being created) and
// shows that reason; `large` is the enlarged empty-envelope zone.
export default function EnvelopeUploadZone({ orgId, envelopeId, status = 'open', disabledNotice = '', large = false, onStored, onSettled, onBusyChange = null, knownSha256s = [], placeOf = null }) {
  const disabled = status === 'closed' || status === 'deleted' || Boolean(disabledNotice);
  const notice = disabledNotice || (status === 'closed' ? CLOSED_UPLOAD_NOTICE : DELETED_NOTICE);
  const [items, setItems] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const storedIdsRef = useRef(new Set());
  // WebUI #240: the sha256 of receipts already in the workspace, read at the moment a file is added.
  const knownRef = useRef([]);
  knownRef.current = knownSha256s;
  // M004-F0777 §2.2: the workspace's envelope list resolves a duplicate's place; read at call time.
  const placeOfRef = useRef(null);
  placeOfRef.current = placeOf;
  const [skipped, setSkipped] = useState([]);

  // M004-F0793 d: the workspace does not refresh while a file is queued or uploading.
  const busyRef = useRef(null);
  busyRef.current = onBusyChange;
  const busyNow = items.some((item) => item.state === 'queued' || item.state === 'uploading');
  useEffect(() => {
    const report = busyRef.current;
    if (typeof report === 'function') report(busyNow);
    return () => { if (typeof report === 'function' && busyNow) report(false); };
  }, [busyNow]);

  const queue = useMemo(() => createUploadQueue({
    orgId,
    envelopeId,
    onChange: setItems,
    knownSha256s: () => knownRef.current,
    onDuplicate: (name) => setSkipped((current) => [...current, name]),
    placeOf: (id) => (placeOfRef.current || duplicatePlace)(id),
    onStepUp: (nextChallenge) => setChallenge(nextChallenge),
    onSettled: (item, summary) => {
      if (item.state === 'stored' && !storedIdsRef.current.has(item.receipt_id)) {
        storedIdsRef.current.add(item.receipt_id);
        if (typeof onStored === 'function') onStored(item.ack, item);
      }
      if (typeof onSettled === 'function') onSettled(item, summary);
    },
  }), [orgId, envelopeId, onStored, onSettled]);

  useEffect(() => {
    storedIdsRef.current = new Set();
    setItems([]);
    setSkipped([]);
    setChallenge(null);
  }, [envelopeId]);

  const addFiles = (fileList) => {
    if (disabled || !fileList || !fileList.length) return;
    queue.addFiles(fileList);
  };

  const summary = useMemo(() => {
    const total = items.filter((item) => item.state !== 'invalid').length;
    const stored = items.filter((item) => item.state === 'stored').length;
    const duplicate = items.filter((item) => item.state === 'duplicate').length;
    return { total, stored, duplicate };
  }, [items]);

  return (
    <section
      className={`env-upload-zone${disabled ? ' env-upload-disabled' : ''}${large ? ' env-upload-large' : ''}`}
      data-testid="envelopes-upload-zone"
      data-upload-disabled={disabled ? 'true' : 'false'}
      aria-disabled={disabled || undefined}
      onDragOver={(event) => {
        if (!disabled) event.preventDefault();
      }}
      onDrop={(event) => {
        if (disabled) return;
        event.preventDefault();
        addFiles(event.dataTransfer?.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ENVELOPE_UPLOAD_ACCEPT}
        disabled={disabled}
        data-testid="envelopes-add-receipts-input"
        aria-label="Add receipts"
        className="env-upload-input"
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        disabled={disabled}
        data-testid="envelopes-take-photo-input"
        aria-label="Take a photo of a receipt"
        className="env-upload-input"
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <div className="env-upload-head">
        <p className="env-upload-hint">Drop receipts here, or</p>
        <button
          type="button"
          className="env-upload-button"
          disabled={disabled}
          data-testid="envelopes-add-receipts-button"
          onClick={() => inputRef.current?.click()}
        >
          Add receipts
        </button>
        <button
          type="button"
          className="env-upload-button env-upload-camera"
          disabled={disabled}
          data-testid="envelopes-take-photo-button"
          onClick={() => cameraRef.current?.click()}
        >
          Take photo
        </button>
        {disabled ? <p className="env-upload-notice" role="status" data-testid="envelopes-upload-notice">{notice}</p> : null}
      </div>

      {skipped.map((name, index) => (
        <p key={`skip-${index}`} className="env-upload-notice" role="status" data-testid="envelopes-upload-duplicate">
          {`${name} is already added — not uploaded again.`}
        </p>
      ))}

      {items.length ? (
        <>
          <p className="env-upload-summary" data-testid="envelopes-upload-summary">
            {summary.stored} of {summary.total} stored{summary.duplicate ? `, ${summary.duplicate} already uploaded` : ''}
          </p>
          <ul className="env-upload-rows" data-testid="envelopes-upload-rows">
            {items.map((item) => (
              <li key={item.key} className={`env-upload-row env-upload-${item.state}`} data-testid={`envelopes-upload-row-${item.key}`}>
                <span className="env-upload-name">{item.name}</span>
                {item.captured_at ? (
                  <span className="env-upload-when">
                    {item.captured_at.slice(0, 16).replace('T', ' ')}
                    {' '}
                    ({captureBadge(item.captured_at_origin)})
                  </span>
                ) : null}
                <span className="env-upload-state">{stateLabel(item)}</span>
                {item.state === 'uploading' ? (
                  <progress
                    className="env-upload-progress"
                    max="100"
                    value={Math.round((item.progress || 0) * 100)}
                    aria-label={`Uploading ${item.name}`}
                    data-testid={`envelopes-upload-progress-${item.key}`}
                  />
                ) : null}
                {item.state === 'failed' || item.state === 'step-up' ? (
                  <button
                    type="button"
                    className="env-upload-retry"
                    data-testid={`envelopes-upload-retry-${item.key}`}
                    onClick={() => queue.retry(item.key)}
                  >
                    Retry
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <StepUpDialog
        challenge={challenge}
        title="Verify to add receipts"
        description="Confirm your identity and the upload continues from where it stopped — nothing needs to be picked again."
        onComplete={() => {
          setChallenge(null);
          queue.resume();
        }}
        onCancel={() => setChallenge(null)}
      />
    </section>
  );
}
